<script setup lang="ts">
/**
 * Base wrapper around PrimeVue's DataTable, to keep things centrally controllable.
 *
 * More slots and props are added as needed. Please use this component instead of using
 * PrimeVue's DataTable directly.
 */
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import KpButton from './KpButton.vue';

export interface KpBaseGridProps {
    title?: string;
    showRecordEditBtn?: boolean;
    showRecordDeleteBtn?: boolean;
    showReloadBtn?: boolean;
    showCsvDownloadBtn?: boolean;
    showAddBtn?: boolean;
    showCopyBtn?: boolean;
    disableAddBtn?: boolean;
}

withDefaults(defineProps<KpBaseGridProps>(), {
    showRecordDeleteBtn: true,
    showRecordEditBtn: true,
    showCsvDownloadBtn: true,
    showAddBtn: true,
    showCopyBtn: false,
    showReloadBtn: true,
    disableAddBtn: false
});

const emit = defineEmits<{
    (e: 'editrecord', value: any): void;
    (e: 'deleterecord', value: any): void;
    (e: 'new'): void;
    (e: 'reload'): void;
    (e: 'copy'): void;
    (e: 'csvdownload'): void;
}>();
</script>
<template>
    <DataTable striped-rows resizable-columns show-gridlines column-resize-mode="fit" size="small">
        <template #header>
            <slot name="header">
                <div class="flex align-items-center justify-content-between gap-2">
                    <h2>{{ title || '' }}</h2>
                    <div class="flex gap-1">
                        <KpButton
                            v-if="showAddBtn"
                            :disabled="disableAddBtn"
                            v-tooltip.bottom="$t('new')"
                            icon="far fa-plus"
                            size="small"
                            severity="success"
                            @click="$emit('new')"
                        />
                        <KpButton
                            v-if="showReloadBtn"
                            icon="far fa-rotate-right"
                            v-tooltip.bottom="$t('reload')"
                            size="small"
                            severity="secondary"
                            @click="$emit('reload')"
                        />
                        <KpButton
                            v-if="showCopyBtn"
                            icon="far fa-copy"
                            v-tooltip.bottom="$t('copy')"
                            size="small"
                            severity="secondary"
                            @click="$emit('copy')"
                        />
                        <KpButton
                            v-if="showCsvDownloadBtn"
                            icon="far fa-file-arrow-down"
                            v-tooltip.bottom="$t('csv_download')"
                            size="small"
                            severity="secondary"
                            @click="$emit('csvdownload')"
                        />
                    </div>
                </div>
            </slot>
        </template>
        <template #groupheader="slotProps">
            <slot name="groupheader" :data="slotProps.data" :index="slotProps.index">foo</slot>
        </template>

        <!-- default slot, passes columns etc. to the DataTable -->
        <slot></slot>
        <!-- slot for after the normal content: defaults to adding a tools column with edit/del btns -->
        <slot name="tools" v-if="showRecordEditBtn || showRecordDeleteBtn">
            <Column style="width: 80px" frozen align-frozen="right">
                <template #body="slotProps">
                    <div>
                        <KpButton
                            v-if="showRecordEditBtn"
                            aria-label="Edit Record"
                            icon="far fa-pen"
                            v-tooltip.bottom="$t('bearbeiten')"
                            size="small"
                            severity="secondary"
                            variant="text"
                            rounded
                            @click="emit('editrecord', slotProps.data)"
                        />
                        <KpButton
                            v-if="showRecordDeleteBtn"
                            aria-label="Delete Record"
                            icon="far fa-trash"
                            v-tooltip.bottom="$t('loeschen')"
                            size="small"
                            severity="secondary"
                            variant="text"
                            rounded
                            @click="emit('deleterecord', slotProps.data)"
                        />
                    </div>
                </template>
            </Column>
        </slot>
    </DataTable>
</template>

<style lang="css" scoped>
h2 {
    margin: 0;
}
</style>
